/* eslint-disable @typescript-eslint/no-unsafe-argument */
import type { GetServerSideProps, NextPage } from 'next'
import { mapBussinessFinder } from '~/components/BusinessFinder/BusinessFinder.mapping'
import { type FloatingAdvertProps } from '~/components/FloatingAdvert/FloatingAdvert.interface'
import { mapFloatingAdvert } from '~/components/FloatingAdvert/FloatingAdvert.mapping'
import { Layout } from '~/components/Layout/Layout'
import { mapLayout } from '~/components/Layout/Layout.mapping'
import {
  mapSearchResults,
  type mapppedMapProps
} from '~/components/Map/Map.mapping'
import { Map } from '~/components/Map/MapMapbox'
import { PageMetadata } from '~/components/PageMetadata/PageMetadata'
import { type PageMetadataProps } from '~/components/PageMetadata/PageMetadata.interface'
import { mapBusinessListingPageMetadata } from '~/components/PageMetadata/PageMetadata.mapping'
import { getSearch, type BusinessSearchItem } from '~/lib/search'
import {
  fetchAllStoryblokContent,
  fetchBusinessesContent
} from '~/lib/storyblok'
import { type ISbStory } from '~/types'
import { type GlobalSettingsStoryblok } from '~/types/generated'

export interface BusinessListingPageProps extends PageMetadataProps {
  story: ISbStory;
  settings: ISbStory;
  advert: FloatingAdvertProps;
  layout: ISbStory;
  finder: ISbStory;
  search: ISbStory;
}

const BusinessListingPage: NextPage<BusinessListingPageProps> = (
  ctx
): JSX.Element => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { metadata, advert, layout, finder, schema, search } = ctx

  // const metaImage = search.results.businessListings[0].image.src || null

  return (
    <>
      <PageMetadata
        metadata={metadata}
        // priorityImage={metaImage}
        schema={schema}
      />
      <Layout layout={layout} businessFinder={finder} pinkHeader>
        <main>
          <Map
            filters={search.results.filters}
            businessListings={search.results.businessListings}
            markers={search.results.markers}
            mapSrc={search.results.mapSrc}
            attribution={search.results.attribution}
            zoom={search.results.zoom}
            center={search.results.center}
            activeBusinessId={search.results.activeBusinessId}
            activeBusinessName={search.results.activeBusinessName}
            footer={layout.footer}
            businessPage={!search.results.filters}
            advertProps={advert}
          />
        </main>
      </Layout>
    </>
  )
}

export const getServerSideProps: GetServerSideProps = async (context) => {
  const { params, query } = context

  if (!params || !('type' in params)) {
    return { notFound: true }
  }

  const slug = Array.isArray(params.type) ? params.type[0] : params.type
  if (typeof slug !== 'string') {
    return { notFound: true }
  }

  if (slug.includes('.')) {
    return { notFound: true }
  }

  const version = context.draftMode ? 'draft' : 'published'

  const getStoryblokStories = await fetchAllStoryblokContent({
    by_slugs: 'global-settings,business-listing',
    resolve_relations: ['globalSettings.footerNavigation', 'globalSettings.advert'],
    version
  })

  const businessesListingData = getStoryblokStories.find(
    (content: { slug: string }) => content.slug === 'business-listing'
  )
  const globalSettingsData = getStoryblokStories.find(
    (content: { slug: string }) => content.slug === 'global-settings'
  )

  if (!businessesListingData || !globalSettingsData) {
    return { notFound: true }
  }

  const businessesData = await fetchBusinessesContent({
    version: 'published'
  })

  if (!businessesData) {
    return { notFound: true }
  }

  const businessCategories = businessesData.map(
    (bussiness: ISbStory) => bussiness.content.businessCategory[0]
  )

  const finder = mapBussinessFinder(
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    businessCategories,
    globalSettingsData.content as GlobalSettingsStoryblok
  )

  const layout = mapLayout(
    globalSettingsData.content as GlobalSettingsStoryblok,
    true
  )

  const searchType = slug
    .split('-')
    .map((word, index) => (index === 0 ? word : word[0].toUpperCase() + word.slice(1)))
    .join('')

  const searchCategoriesForSearch = (query?.categories as string) ?? null
  const searchCategories = searchCategoriesForSearch
    ? searchCategoriesForSearch.split(',')
    : []

  const businessId = query.id ? (query.id as string) : null
  const businessName = query.name ? (query.name as string) : null

  const searchParams = {
    type: slug,
    categories: searchCategories,
    sort: (query?.orderBy as string) || ''
  }

  const searchData = getSearch(
    businessesData as unknown as BusinessSearchItem[],
    slug,
    searchCategories,
    searchParams.sort
  )

  if (searchData.results.length === 0) {
    return {
      notFound: true
    }
  }

  const canonicalSlug = businessName
    ? `businesses/${slug}/${businessName}`
    : `businesses/${slug}`

  const metadata = mapBusinessListingPageMetadata(
    businessesListingData.content.seoMetadata[0],
    canonicalSlug,
    globalSettingsData.content as GlobalSettingsStoryblok,
    searchParams,
    businessCategories
  )

  const mappedSearchData = mapSearchResults(
    searchData as mapppedMapProps,
    searchParams,
    businessId,
    businessName,
    businessCategories
  )

  const itemList: Array<Record<string, unknown>> =
  mappedSearchData.businessListings.map((business) => ({
    '@type': 'ListItem',
    position: business.index,
    url: business.url ? `${process.env.BASE_URL}${business.url}` : ''
  }))

  interface BussinessCategory {
    categoryName: { name: string;
      value: string;
    };
  }

  const type = (businessCategories as BussinessCategory[])
    .filter((category) => category.categoryName.value === params.type?.toString())[0]

  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: `Independent ${type.categoryName.name} businesses in Witney`,
    itemListElement: itemList
  }

  const advert = mapFloatingAdvert(globalSettingsData?.content?.advert?.[0]?.content)
  return {
    props: {
      metadata,
      story: businessesListingData,
      layout,
      finder,
      advert,
      schema,
      search: {
        searchType,
        searchCategoriesForSearch,
        searchParams,
        businessId,
        businessName,
        categories: businessCategories,
        results: mappedSearchData
      }
    }
  }
}

export default BusinessListingPage
